<template lang="pug">
aiq-dialog.schedules-dialog(title="Calendar"
            :width="'80%'"
            :model-value="true"
            :show-close="false"
            :close-on-press-escape="true"
            :close-on-click-modal="true"
            :before-close="handleBeforeClose"
            modal-class="schedules-dialog-overlay")

  .external-integrations(v-if="showOutlookIcon && showTablePage && !showMeetingItem")
    .ms-outlook-buttons
      MsOutlookIcon.outlook-icon(@click="onOutlookCheck")
      font-awesome-icon.outlook-not-connected(v-if="!hasOutlookConnection" :icon="['fas', 'exclamation']" size='lg')
      outlook-integration(v-if="tryOutlook"
                          :auth-config="outlookAuthConfig.oauth"
                          @auth-success="onAuthSuccess"
                          @auth-failure="onAuthFailure")

  .view-schedules(v-if="showTablePage && !this.showOnlyForm && !showMeetingItem")
    calendar(:schedules="schedules"
             :period="visibleCalendar"
             :useNewCalendar="true"
             @change="fetchMonthSchedules"
             @item-click="onEventSelected"
             @cell-click="onCalendarCellClick")
  .view-meeting-item(v-else-if="showMeetingItem")
    meeting-item(:event="schedule")

  .edit-schedules(v-else)
    .title
      h1  {{ editHeadingStr }}
    .form-body
      aiq-form(label-width="150px"
                :hide-required-asterisk="true"
                class="has-default-form-items")
        aiq-form-item(label="Type" required)
          aiq-select(v-model="schedule.event_type"
                        placeholder="Select Reminder Type"
                        size="small"
                        :clear-icon="Icon.CircleCloseFilled"
                        :suffix-icon="Icon.CaretBottom"
                        :disabled="isScheduleRun")
            aiq-option(v-for="item in supportingEvents"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value")
        aiq-form-item(label="Start Date" required)
          aiq-col(:span="12")
            aiq-date-picker(v-model="dateObj"
                            type="date"
                            popper-class="date-picker-popup"
                            size="small"
                            :disabled-date="disabledDate"
                            :clearable="false"
                            placeholder="Select date"
                            format="MM/DD/YYYY"
                            :disabled="isScheduleRun")
        aiq-form-item(label="Start Time" required)
          aiq-col(:span="12")
            aiq-time-select.time-pick-input(v-model="timeStr"
                    size="small"
                    popper-class="time-picker-popup"
                    :start="startTime"
                    step="00:15"
                    end="23:45"
                    format="HH:mm"
                    placeholder="Select time"
                    :clearable="false"
                    :suffix-icon="Icon.CaretBottom"
                    :disabled="!dateObj || isScheduleRun ")

        aiq-form-item(v-if="showRecurrenceConfig")
          template(#label)
            | Recurrence
            span.optional-text (optional)
          aiq-select(v-model="recurrenceMode"
                    size="small"
                    @change='onRecurrenceChange'
                    :suffix-icon="Icon.CaretBottom"
                    :clear-icon="Icon.CircleCloseFilled")
            aiq-option(v-for="recurrence in recurrenceMenu"
                       :key="recurrence.value"
                       :label="recurrence.label"
                       :value="recurrence.value"
                       @click="onRecurrenceOptionClick(recurrence.value)")
        recurrence-dialog(v-if="showRecurrenceConfigUI"
                          :model-value="recurrence"
                          @submit="onRecurrenceSave"
                          @cancel="onRecurrenceCanceled"
                          @update:freq="onRecurrenceFreqUpdate")
        aiq-form-item(v-if="this.schedule.customer_id" label="Customer")
          aiq-button.zero-padding(link type="primary" @click="goToConversation") {{ customerName }}
        aiq-form-item(label="Title" required)
          aiq-input(v-model="schedule.title"
                    placeholder="Title"
                    size="small"
                    :disabled="isScheduleRun")
        aiq-form-item(label="Description")
          template(#label)
            | Description
            span.optional-text (optional)
          .custom-rich-text-container
            RichText(@update="onUpdateRichTextEditor"
                  :showImageUpload="false"
                  :inChat="false"
                  placeHolder="Description"
                  :readOnlyMode="isScheduleRun")


  aiq-dialog(:model-value="showRecurrenceUpdateModeSelection"
             :title="isDeleteOperation ? 'Delete Recurring Reminder' : 'Edit Recurring Reminder'"
             @closed="showRecurrenceUpdateModeSelection = false"
             :close-on-click-modal="true"
             :close-on-press-escape="true"
             :before-close="handleRecurrenceUpdateDialogBeforeClose"
             append-to-body)
      aiq-radio-group(v-model="recurrenceUpdateMode")
        aiq-radio(v-for="option in recurrenceUpdateModeOptions"
                  :value="option.value"
                  :label="option.label")
      template(#footer)
        aiq-button(@click="onRecurrenceDialogCancel") Cancel
        aiq-button(type="primary" @click="onRecurrenceUpdateModeSelected" :disabled="!recurrenceUpdateMode") Continue

  template(v-slot:footer)
    .table-page-footer(v-if="showTablePage && !this.showOnlyForm && !showMeetingItem")
      aiq-button(@click="close") Close
      aiq-button(type="primary" @click="showCreateOrEditSchedule()") New Reminder
    .edit-page-footer(v-else)
      aiq-button(v-if="isEditing && !showMeetingItem" type="danger" @click="cancelSchedule") Delete
      aiq-button(v-if="this.showOnlyForm" @click="close") Close
      aiq-button(v-if="!this.showOnlyForm" @click="cancelEdit") Cancel
      aiq-button(v-if="!isScheduleRun" type="primary"
                 @click="createOrUpdateSchedule"
                 :disabled="!isScheduleSavable || !isEdited") Save
</template>

<script>
import get from 'lodash/get';
import set from 'lodash/set';
import has from 'lodash/has';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import omit from 'lodash/omit';
import values from 'lodash/values';
import flattenDepth from 'lodash/flattenDepth';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';
import dayjs from '@/localPlugins/dayjs';
import { mapActions, mapState } from 'vuex';
import * as log from 'loglevel';
import { RRule } from 'rrule';
import { SCHEDULE_TYPE } from '@/constants/analytics';
import OutlookIntegration from './OutlookIntegration';

import { getCustomerName, confirmBeforeDialogClose } from '@/libs';
import mitt from 'mitt';
import RichText from '@/components/RichText/RichText.vue';
import Calendar from './Calendar.vue';
import MeetingItem from './MeetingItem.vue';
import RecurrenceDialog from './RecurrenceDialog.vue';
import MsOutlookIcon from '@/assets/svg/ms-outlook.vue';
import { RECURRENCE_EDIT_MODE } from '@/constants/agent';

const SUPPORTED_SCHEDULES = [
  {
    label: 'Reminder Notification',
    value: 'reminder',
  },
];


const SUPPORTED_SCHEDULES_TYPE_TO_LABEL = SUPPORTED_SCHEDULES.reduce((lookup, obj) => {
  lookup[obj.value] = obj.label;
  return lookup;
}, {});

const TIME_INTERVAL = 15;

function getMomentNextTimeCycle(dateObj) {
  let addition = TIME_INTERVAL - (dateObj.getMinutes() % TIME_INTERVAL);
  // make sure the time is in future
  addition = (addition === 0) ? TIME_INTERVAL : addition;
  return dayjs(dateObj).add(addition, 'minutes').set({ second: 0, millisecond: 0});
}

function generateNewISOString(isoString, hour, minute) {
  return dayjs(isoString)
    .set('hour', hour)
    .set('minute', minute)
    .set('second', 0)
    .set('millisecond', 0)
    .toISOString();
}

function emptySchedule() {
  return {
    title: '',
    description: '',
    event_type: 'reminder',
    event_data: {},
    customer_id: null,
    integration_id: null,
    execute_at: getMomentNextTimeCycle(new Date()).toISOString(),
  };
}

const YESTERDAY_DATEOBJECT = dayjs().subtract(1, 'days').toDate();

function isToday(date) {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
}

const EDIT_MODE = {
  CREATE: 'create',
  UPDATE: 'update',
  DONE: 'done',
};

export default {
  name: 'SchedulesDialog',
  components: {
    Calendar,
    MsOutlookIcon,
    OutlookIntegration,
    MeetingItem,
    RecurrenceDialog,
    RichText,
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  computed: {
    ...mapState({
      schedulesByMonth: state => state.agent.schedules,
      supportRecurringReminder: state => state.featureFlags.FEATURE_FLAGS.RECURRING_REMINDER,
    }),

    showOutlookIcon() {
      return this.canView('/global/calendar/ms-outlook') && get(this.outlookAuthConfig, 'enabled', false);
    },

    schedulesLoggin() {
      const schedules = cloneDeep(this.schedule);
      schedules.type = this.showOnlyForm ? SCHEDULE_TYPE.NOTES : SCHEDULE_TYPE.CALENDAR;
      return schedules;
    },

    schedules() {
      if (this.visibleCalendar.year === 0) {
        return [];
      }

      // Generate merged items for all collected items

      // flatten Aiq items & Integration items
      const flattenedItems = flattenDepth([
        values(this.schedulesByMonth),
        values(this.integrationSchedulesByMonth),
      ], 2); // eslint-disable-line no-magic-numbers

      // Remove duplicates
      return uniqBy(flattenedItems, item => item.id);
    },

    calendarKey() {
      return `${this.visibleCalendar.year}-${this.visibleCalendar.month}`;
    },

    supportingEvents() {
      return SUPPORTED_SCHEDULES;
    },

    isEditing() {
      return this.editMode === EDIT_MODE.UPDATE;
    },

    isScheduleRun() {
      return this.editMode === EDIT_MODE.DONE;
    },

    editHeadingStr() {
      if (this.editMode === EDIT_MODE.CREATE) {
        return 'New Reminder';
      }

      if (this.editMode === EDIT_MODE.UPDATE) {
        return 'Edit Reminder';
      }

      if (this.editMode === EDIT_MODE.DONE) {
        return 'View Reminder';
      }

      return '';
    },

    editMode() {
      if (get(this.schedule, 'id', -1) === -1) {
        return EDIT_MODE.CREATE;
      }

      // Note: This is to disable editing old reminder that never worked.
      //       If we want to add the functionality, just uncomment this.
      // if (get(this.schedule, 'executed_at', null)) {
      //   return EDIT_MODE.DONE;
      // }

      return EDIT_MODE.UPDATE;
    },

    isEdited() {
      return !isEqual(this.schedule, this.originalSchedule);
    },

    isScheduleSavable() {
      if (!this.schedule) {
        return false;
      }

      if (!this.schedule.title || this.schedule.title.trim() === '') {
        return false;
      }

      if (!isNil(this.schedule.recurrence_id)) {
        return true;
      }

      return this.schedule.execute_at && this.schedule.event_type
        && dayjs(this.schedule.execute_at).isAfter(dayjs());
    },

    disabledDate() {
      return (date) => (date <= YESTERDAY_DATEOBJECT);
    },

    startTime() {
      if (this.dateObj && isToday(this.dateObj)) {
        // should change start time as it needs to show future time.
        return getMomentNextTimeCycle(new Date()).format('HH:mm');
      }
      return '00:00';
    },

    dateObj: {
      set(newDateObj) {
        if (newDateObj) {
          this.schedule.execute_at = dayjs(this.schedule.execute_at)
            .set('year', newDateObj.getFullYear())
            .set('month', newDateObj.getMonth())
            .set('date', newDateObj.getDate())
            .toISOString();
        }
      },
      get() {
        return (this.schedule) ? dayjs(this.schedule.execute_at).toDate() : null;
      },
    },

    timeStr: {
      set(newTimeStr) {
        if (newTimeStr) {
          const NOON = 12;
          let hour, minute;
          if (newTimeStr.includes('AM') || newTimeStr.includes('PM')) {
            const timeParts = newTimeStr.split(' ');
            const [h, m] = timeParts[0].split(':').map(s => parseInt(s, 10));
            const period = timeParts[1];
            hour = h;
            if (period === 'PM' && h !== NOON) {
              hour = h + NOON;
            } else if (period === 'AM' && h === NOON) {
              hour = 0;
            }
            minute = m;
          } else {
            [hour, minute] = newTimeStr.split(':').map(s => parseInt(s, 10));
          }
          this.schedule.execute_at = generateNewISOString(this.schedule.execute_at, hour, minute);

          if (has(this.schedule, 'recurrence.dtstart')
            && !isEmpty(this.schedule.recurrence.dtstart)
            && !isNil(this.schedule.recurrence.dtstart)) {
            this.schedule.recurrence.dtstart = generateNewISOString(this.schedule.recurrence.dtstart, hour, minute);
          }

          if (has(this.schedule, 'recurrence.until')
            && !isEmpty(this.schedule.recurrence.dtstart)
            && !isNil(this.schedule.recurrence.dtstart)) {
            this.schedule.recurrence.until = generateNewISOString(this.schedule.recurrence.until, hour, minute);
          }
        }
      },
      get() {
        return (this.schedule) ? dayjs(this.schedule.execute_at).format('HH:mm') : '';
      },
    },
    recurrence: {
      set(newConfig) {
        this.schedule.recurrence = newConfig;
      },
      get() {
        if (!isEmpty(this.schedule.recurrence)) {
          return this.schedule.recurrence;
        }

        return {
          dtstart: this.schedule.execute_at,
          freq: this.recurrenceMode,
          tzid: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      },
    },
    customerName() {
      return getCustomerName(get(this.schedule, 'event_data.customer', {}));
    },

    recurrenceUpdateModeOptions() {
      return [
        {
          value: RECURRENCE_EDIT_MODE.THIS_ITEM,
          label: 'This event',
        },
        {
          value: RECURRENCE_EDIT_MODE.THIS_AND_FOLLWOING_ITEMS,
          label: 'This and all following events',
        },
        {
          value: RECURRENCE_EDIT_MODE.ALL_ITEMS,
          label: 'All events in the series',
        },
      ];
    },

    recurrenceMenu() {
      return [
        {
          value: -1, // no recurrence
          label: 'Do not repeat',
        },
        {
          value: RRule.DAILY,
          label: 'Repeat daily',
        },
        {
          value: RRule.WEEKLY,
          label: 'Repeat weekly',
        },
        {
          value: RRule.MONTHLY,
          label: 'Repeat monthly',
        },
        {
          value: RRule.YEARLY,
          label: 'Repeat yearly',
        },
      ];
    },
    showRecurrenceConfig() {
      return this.supportRecurringReminder
        && this.recurrenceUpdateMode !== RECURRENCE_EDIT_MODE.THIS_ITEM;
    },
    // return equivalent outlook item
    // when an our item is selected
    externalCalendarPayload() {
      let externalCalendarPayload = null;

      if (this.recurrenceUpdateMode !== null) {
        externalCalendarPayload = flatten(values(this.integrationSchedulesByMonth))
          .find(item => item.execute_at === this.recurringTime);
        externalCalendarPayload = pick(externalCalendarPayload, ['title', 'description', 'integration_id', 'execute_at']);
      }

      return externalCalendarPayload;
    },
  },
  props: {
    showOnlyForm: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      recurrenceMode: -1,
      showRecurrenceConfigUI: false,
      showTablePage: true,
      showMeetingItem: false,
      schedule: emptySchedule(),
      originalSchedule: null,
      visibleCalendar: {
        month: 0,
        year: 0,
      },
      tryOutlook: false,
      hasOutlookConnection: false,
      integrationSchedulesByMonth: {},
      outlookAuthConfig: null,
      showRecurrenceUpdateModeSelection: false,
      recurringTime: null,
      recurrenceUpdateMode: null,
      isDeleteOperation: false,
      richTextEventEmitter: mitt(),
    };
  },
  async mounted() {
    if (this.formData) {
      this.schedule = merge({}, this.schedule, pick(this.formData, ['id', ...Object.keys(this.schedule)]));

      if (this.schedule.id) {
        this.showCreateOrEditSchedule(this.schedule);
      }

      this.recurrenceMode = get(this.schedule, 'recurrence.freq', -1);
    }

    this.outlookAuthConfig = await this.$store.dispatch('integrations/getIntegration', { name: 'ms-outlook' });
    if (this.showOutlookIcon) {
      // check outlook connection
      this.hasOutlookConnection = await this.$store.dispatch('integrations/checkIntegrationConnection', 'ms-outlook');
    }

    const m = dayjs();
    this.visibleCalendar = { month: m.month() + 1, year: m.year() };
    return this.fetchMonthSchedules(this.visibleCalendar);
  },
  watch: {
    showTablePage: {
      handler(newVal) {
        if (!newVal && this.schedule.description) {
          this.$nextTick(() => {
            this.richTextEventEmitter.emit('setText', this.schedule.description || '');
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      selectSuggestions: 'conversations/selectSuggestions',
      obfuscateMessage: 'conversations/obfuscateMessage',
    }),

    async fetchMonthSchedules(date) {
      this.visibleCalendar = date;
      const query = { ...date, time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone };

      await this.$store.dispatch('agent/fetchScheduledJobs', query);

      if (this.hasOutlookConnection) {
        this.integrationSchedulesByMonth[this.calendarKey] =
          (await this.$store.dispatch('integrations/getCalendarEvents', { integration: 'ms-outlook', query }))
            .models
            .map(item => {
              item.id = item.integration_id;
              return item;
            });
      }
    },

    getEventTypeLabel(eventType) {
      return SUPPORTED_SCHEDULES_TYPE_TO_LABEL[eventType];
    },

    getTimeFormatted(timeStr) {
      return dayjs(timeStr).format('MMM DD, YYYY hh:mm A');
    },

    getSchedules() {
      if (!isEmpty(this.schedule.recurrence)) {
        this.schedule.recurrence = omitBy(this.schedule.recurrence, isNil);
      }

      return {
        schedule: omitBy(this.schedule, isNil), // Clean up null to request to our backend
        schedulesLoggin: this.schedulesLoggin,
        shouldSyncOutlook: this.hasOutlookConnection,
        recurrenceUpdateMode: this.recurrenceUpdateMode,
        externalCalendarPayload: this.externalCalendarPayload,
      };
    },

    handleBeforeClose() {
      // If on calendar view (showTablePage is true), close the dialog
      if (this.showTablePage && !this.showOnlyForm) {
        this.close();
        return;
      }

      // If on edit form and form is not edited, redirect to calendar view
      if (!this.showTablePage && !this.isEdited) {
        this.showTablePage = true;
        return;
      }

      // If form is edited, show confirmation
      if (this.isEdited) {
        confirmBeforeDialogClose(this, () => {
          if (this.showOnlyForm) {
            this.close();
          } else {
            this.cancelEdit();
          }
        });
      }
    },

    close() {
      const type = this.showOnlyForm ? SCHEDULE_TYPE.NOTES : SCHEDULE_TYPE.CALENDAR;
      this.$store.dispatch('agent/changeDialogVisibility', { name: 'schedulesDialog', visible: false, type });
    },

    onEventSelected({ eventType, schedule, recurringTime }) {
      if (eventType === 'single') {
        return this.showCreateOrEditSchedule(schedule);
      }

      // Should be recurring item - go directly to edit form
      if (!recurringTime) {
        throw new Error('RecurringTime should not be empty');
      }
      this.recurringTime = recurringTime;
      this.schedule = cloneDeep(schedule);
      this.originalSchedule = cloneDeep(schedule);
      this.recurrenceMode = get(this.schedule, 'recurrence.freq', -1);
      // Reset recurrenceUpdateMode so dialog will show on Save
      this.recurrenceUpdateMode = null;
      this.showTablePage = false;
    },

    handleRecurrenceUpdateDialogBeforeClose(done) {
      // If no mode is selected, close without warning
      if (!this.recurrenceUpdateMode) {
        this.onRecurrenceDialogCancel();
        done();
        return;
      }

      confirmBeforeDialogClose(
        this,
        () => {
          this.onRecurrenceDialogCancel();
          done();
        },
        () => {
          // User cancelled - keep the dialog open (don't call done)
        },
      );
    },

    onRecurrenceDialogCancel() {
      // User cancelled the dialog, reset and close
      this.showRecurrenceUpdateModeSelection = false;
      this.recurrenceUpdateMode = null;
      this.isDeleteOperation = false;
    },

    async onRecurrenceUpdateModeSelected() {
      // User selected a mode and clicked Continue, now proceed with save or delete
      if (!this.recurrenceUpdateMode) {
        return;
      }
      this.showRecurrenceUpdateModeSelection = false;
      // Continue with the operation (save or delete)
      if (this.isDeleteOperation) {
        await this.performDelete();
      } else {
        await this.performSave();
      }
    },

    onCalendarCellClick({ dateTime }) {
      const newEmptySchedule = emptySchedule();
      const parsedDateTime = dayjs(new Date(dateTime)).set({ minute: 0, second: 0, millisecond: 0 });
      const minValidDateTime = dayjs().set({ minute: 0, second: 0, millisecond: 0 });
      const now = dayjs();

      if (parsedDateTime.isAfter(now)) {
        newEmptySchedule.execute_at = parsedDateTime.toISOString();
      }
      else if (parsedDateTime.isBefore(minValidDateTime)) {
        // Invalid date time - do not show dialog
        return;
      }
      else {
        // It is today - find the closest available time slot from now
        const nextTimeSlot = getMomentNextTimeCycle(now.toDate());
        newEmptySchedule.execute_at = nextTimeSlot.toISOString();
      }

      this.showCreateOrEditSchedule(newEmptySchedule);
    },

    showCreateOrEditSchedule(schedule) {
      if (schedule) {
        this.schedule = cloneDeep(schedule);
        this.originalSchedule = cloneDeep(schedule);
        this.recurrenceMode = get(this.schedule, 'recurrence.freq', -1);
        if (get(schedule, 'event_type', 'reminder') === 'meeting') {
          this.showMeetingItem = true;
          return;
        }
      } else {
        // new reminder event
        const newEmptySchdule = emptySchedule();
        this.schedule = cloneDeep(newEmptySchdule);
        this.originalSchedule = cloneDeep(newEmptySchdule);
      }
      this.showTablePage = false;
    },

    cancelEdit() {
      this.schedule = emptySchedule();
      this.originalSchedule = null;
      this.showTablePage = true;
      this.showMeetingItem = false;
      this.recurrenceMode = -1;
      this.recurrenceUpdateMode = null;
      this.recurringTime = null;
      this.showRecurrenceUpdateModeSelection = false;
      this.isDeleteOperation = false;
    },

    async cancelSchedule() {
      if (!this.schedule.id) {
        return;
      }

      // Check if this is a recurring event that needs update mode selection
      if (!isNil(this.schedule.recurrence_id) && isNil(this.recurrenceUpdateMode)) {
        // Show the dialog to select delete mode
        this.isDeleteOperation = true;
        this.showRecurrenceUpdateModeSelection = true;
        return;
      }

      // Proceed with delete
      await this.performDelete();
    },

    async performDelete() {
      if (!this.schedule.id) {
        return;
      }

      try {
        if (!isNil(this.recurrenceUpdateMode) && this.recurrenceUpdateMode !== RECURRENCE_EDIT_MODE.ALL_ITEMS) {
          if (this.recurrenceUpdateMode === RECURRENCE_EDIT_MODE.THIS_ITEM) {
            this.originalSchedule.recurrence.exclude = get(this.originalSchedule.recurrence, 'exclude', []);
            this.originalSchedule.recurrence.exclude.push(this.schedule.execute_at);
          } else {
            // Update until of original item with the day - 1
            this.originalSchedule.recurrence.until = dayjs(this.schedule.execute_at).add('-1', 'day').toISOString();
          }
          this.schedule = this.originalSchedule;
          this.schedule = await this.$store.dispatch('agent/updateScheduledJob', this.getSchedules());
        } else {
          if (!isNil(this.schedule.recurrence_id)) {
            try {
              await this.showRecurrenceChangeConfirmation();
              // Proceed: let it go
            } catch (err) {
              // Cancel: Do nothing
              return;
            }
          }
          await this.$store.dispatch('agent/cancelScheduledJob', this.getSchedules());
        }
        this.$aiq.notify.success('Success');
      } catch (err) {
        log.error(err);
        this.$aiq.notify.error('Unable to cancel the schedule');
      }

      this.cancelEdit();

      if (this.showOnlyForm) {
        await this.$store.dispatch('conversations/refreshSchedule');
        this.close();
      }
    },

    showRecurrenceChangeConfirmation() {
      return this.$aiq.confirm(
        'Delete Recurring Reminder',
        'Deleting this reminder will affect all related reminders. Do you want to proceed?',
        { confirmButtonText: 'Proceed' },
      );
    },

    async createOrUpdateSchedule() {
      // Check if this is a recurring event that needs update mode selection
      if (this.schedule.id && !isNil(this.schedule.recurrence_id) && isNil(this.recurrenceUpdateMode)) {
        // Show the dialog to select update mode
        this.isDeleteOperation = false;
        this.showRecurrenceUpdateModeSelection = true;
        return;
      }

      // Proceed with save
      await this.performSave();
    },

    async performSave() {
      try {
        if (this.schedule.id) {
          if (this.recurrenceUpdateMode === RECURRENCE_EDIT_MODE.THIS_ITEM) {
            const overrideData = get(this.originalSchedule, 'recurrence.override_data', {});
            const newValueSet = {};
            if (this.recurringTime !== this.schedule.execute_at) {
              // Schedule time changed
              // set override data
              // {
              //    [original recurring time with iso sting]: { execute_at: new_time }
              // }
              set(newValueSet, 'execute_at', this.schedule.execute_at);
            }

            if (!isEqual(this.originalSchedule.title, this.schedule.title)) {
              set(newValueSet, 'title', this.schedule.title);
            }

            if (!isEqual(this.originalSchedule.description, this.schedule.description)) {
              set(newValueSet, 'description', this.schedule.description);
            }

            overrideData[this.recurringTime] = newValueSet;
            set(this.originalSchedule, 'recurrence.override_data', overrideData);
            this.schedule = this.originalSchedule;
            this.schedule = await this.$store.dispatch('agent/updateScheduledJob', this.getSchedules());
          } else if (this.recurrenceUpdateMode === RECURRENCE_EDIT_MODE.THIS_AND_FOLLWOING_ITEMS) {
            // Update this and following items of a recurrence item.

            const newSchedule = omit(cloneDeep(this.schedule), ['id', 'recurrence_id', 'recurrence.id']);
            // Update until of original item with the day - 1
            this.originalSchedule.recurrence.until = dayjs(this.schedule.execute_at).add('-1', 'day').toISOString();
            this.schedule = this.originalSchedule;
            this.schedule = await this.$store.dispatch('agent/updateScheduledJob', this.getSchedules());
            // Create new item for new
            this.schedule = newSchedule;
            this.schedule = await this.$store.dispatch('agent/createScheduledJob', this.getSchedules());
          } else {
            this.schedule = await this.$store.dispatch('agent/updateScheduledJob', this.getSchedules());
          }
        } else {
          this.schedule = await this.$store.dispatch('agent/createScheduledJob', this.getSchedules());
        }

        this.$aiq.notify.success('Success');
      } catch (err) {
        log.error(err);
        this.$aiq.notify.error('Failed');
      }

      this.cancelEdit();

      if (this.showOnlyForm) {
        await this.fetchMonthSchedules(this.visibleCalendar);
        this.close();
      }
    },

    goToConversation() {
      this.close();
      if (get(this.schedule, 'event_data.customer')) {
        this.$router.push({
          name: 'ConversationByCustomerId',
          params: { id: get(this.schedule, 'event_data.customer.id') },
        });
      }
    },

    onOutlookCheck() {
      if (!this.hasOutlookConnection) {
        this.tryOutlook = true;
      }
    },

    onAuthFailure() {
      this.$aiq.notify.error('Authentication Failed');
    },

    onAuthSuccess() {
      this.hasOutlookConnection = true;
      return this.fetchMonthSchedules(this.visibleCalendar);
    },

    async onIntegrationSync(schedule, shouldDelete = false) {
      // Sync an item to outlook
      const res = await this.$store.dispatch('integrations/syncToExternalCalendar',
        { integration: 'ms-outlook', payload: schedule, shouldDelete });
      log.debug(res);
      return res;
    },
    onRecurrenceChange() {
      if (this.recurrenceMode !== -1) {
        this.showRecurrenceConfigUI = true;
      }
      this.schedule.recurrence = null;
    },

    onRecurrenceSave(newRecurrenceConfig) {
      this.showRecurrenceConfigUI = false;
      this.schedule.recurrence = newRecurrenceConfig;
      // Update recurrenceMode to match the saved config's frequency
      this.recurrenceMode = get(newRecurrenceConfig, 'freq', -1);
    },

    onRecurrenceFreqUpdate(newFreq) {
      // Update recurrenceMode when freq changes in RecurrenceDialog
      this.recurrenceMode = newFreq;
    },

    onRecurrenceCanceled() {
      this.showRecurrenceConfigUI = false;
      this.schedule = cloneDeep(this.originalSchedule);
      this.recurrenceMode = get(this.schedule, 'recurrence.freq', -1);
    },

    onRecurrenceOptionClick(val) {
      if (val === this.recurrenceMode && val !== -1) {
        this.showRecurrenceConfigUI = true;
      }
    },

    onUpdateRichTextEditor(description) {
      this.schedule.description = description.text === '' ? '' : description.html;
    },
  },
};
</script>

<style lang="scss">
.schedules-dialog {
  margin-top: 10px;
}
.schedules-dialog .el-dialog__footer{
  padding: 0 16px 16px 16px;
}
</style>

<style lang="scss" scoped>
@import "../../../styles/aiq-variables.scss";

.view-schedules {
  height: calc(100vh - 205px);
  max-height: 1075px;

  & > .section {
    height: 100%;
  }
}

.title {
  margin-bottom: 20px;
}

.time-range {
  width: 200px;
}

.date-picker-popup {
  width: 240px;
  height: 100px;
}

.data-picker-popup {
  width: 240px;
  height: 100px;
}

.form-body {
  :deep(.el-input.el-input__inner .el-input__prefix .el-input__icon) {
    margin-right: unset;
    bottom: 0px;
    right: -10px;
  }
}

.dash {
  text-align: center;
}

.zero-padding {
  padding: 0px;
}

.external-integrations {
  display: flex;
  justify-content: end;
}
.outlook-icon {
  height: 32px;
  width: 32px;
}

.ms-outlook-buttons {
  position: relative;
}

.outlook-not-connected {
  position: absolute;
  bottom: 5px;
  right: 5px;
  color: #ff9900;
}

:deep(.vuecal__cells.month-view > .vuecal__flex) {
  height: 1px; // to prevent the flex container from growing
}
:deep(.vuecal__cells.month-view .vuecal__cell) {
  height: 16.67% !important;
}
:deep(.vuecal__cells.month-view .vuecal__cell.vuecal__cell--has-events) {
  overflow: hidden;
}
:deep(.vuecal__cells.month-view .vuecal__cell.vuecal__cell--has-events .vuecal__cell-content) {
  justify-content: flex-start;
}
:deep(.vuecal__cells.month-view .vuecal__cell.vuecal__cell--has-events .vuecal__cell-content .vuecal__cell-events) {
  overflow-x: hidden;
  overflow-y: auto;
}

:deep(.vuecal--month-view .vuecal__event-time) {
  font-size: .80em;
}

:deep(.el-textarea__inner) {
  box-shadow: none !important;
}

.schedules-dialog {
  .edit-schedules {
    :deep(.el-select__wrapper.is-focused) {
      box-shadow: 0 0 0 1px $aiq-input-border-focus inset !important;
    }

    .time-pick-input {
      :deep(.el-select__wrapper) {
        gap: 11px !important;
      }
    }
  }
}
.custom-rich-text-container {
  line-height: 17px;
  width:100%;
  :deep(.ProseMirror) {
    min-height: 80px !important;
    max-height: 80px !important;
  }
}
</style>
